import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { videos } from '@/mocks/videos';
import { categoryTabs, categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SeoHead from '@/components/feature/SeoHead';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  const filteredVideos = activeTab === 'all'
    ? videos
    : videos.filter((v) => v.category === activeTab);

  const currentCategory = categories.find((c) => c.slug === activeTab);
  const tabLabel = activeTab === 'all' ? '全部' : currentCategory?.name || activeTab;

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: tabLabel },
  ];

  const pageTitle = activeTab === 'all'
    ? '全部视频 - 蜜桃视频 | 高清视频内容聚合'
    : `${tabLabel}视频 - 蜜桃视频 | ${tabLabel}高清视频在线观看`;

  const pageDesc = activeTab === 'all'
    ? '浏览蜜桃视频全部精选高清内容，涵盖生活、娱乐、美食、旅行、科技、时尚等多元类目，发现你的专属精彩。'
    : `${currentCategory?.description || ''}蜜桃视频${tabLabel}频道汇聚精选${tabLabel}类高清视频，画质清晰，内容优质，立即探索。`;

  const pageKeywords = activeTab === 'all'
    ? '蜜桃视频,蜜桃视频高清在线,蜜桃视频平台,精选短视频平台,高清视频内容聚合'
    : `蜜桃视频${tabLabel},${tabLabel}视频,${tabLabel}高清在线,蜜桃视频平台`;

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://mitaovideo.com/category/${activeTab}`}
      />

      <div className="w-full px-4 md:px-6 pt-24 pb-12 md:pb-16 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8 md:mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {activeTab === 'all' ? '全部视频' : tabLabel}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-600">{currentCategory.description}</p>
            )}
          </div>

          <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categoryTabs.map((tab) => (
              <Link
                key={tab.slug}
                to={`/category/${tab.slug}`}
                onClick={() => setActiveTab(tab.slug)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === tab.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {tab.name}
              </Link>
            ))}
          </div>

          {filteredVideos.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-video-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {filteredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full aspect-video overflow-hidden">
                    <img
                      src={video.imageUrl}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="text-xs bg-primary-500 text-background-50 px-2 py-0.5 rounded-full">
                        {video.categoryName}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {formatViews(video.views)}
                      </span>
                      <span>{video.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}